<template>
  <div class="mb-5 d-flex align-center justify-space-between">
    <div>
      <div class="headline6 mb-2" v-text="settings.region" />
      <div class="body1 mb-4">
        <span class="text--semitransparent mr-3">
          {{ $t("network.virtualOffice.ipAddresses", settings.servers.length) }}: {{ ips }}</span
        >
        <span v-if="hasManyServers" role="button" class="body2 coro-link" @click="onShowAllIpsClick"
          >{{ $t("general.viewAll") }} ›</span
        >
      </div>
    </div>
  </div>
  <v-card>
    <v-card-text class="px-8">
      <v-form ref="form">
        <v-switch
          :model-value="enabled"
          class="v-input--reverse"
          color="primary lighten-1"
          hide-details
          :ripple="false"
          @click.prevent="onActionClick({ action: ZtnaAction.TOGGLE_ZTNA })"
        >
          <template #label>
            <span
              class="subtitle1 text--black mr-2"
              v-text="$t('network.virtualOffice.ztna.ztnaActivationLabel')"
            />
          </template>
        </v-switch>
        <span class="body2" v-text="$t('network.virtualOffice.ztna.ztnaActivationDescription')"
      /></v-form>
    </v-card-text>
  </v-card>
  <div class="d-flex justify-space-between align-center mt-8 mb-4">
    <span class="subtitle1" v-text="$t('network.virtualOffice.ztna.ztnaResourceList')" />
    <v-menu>
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          class="add-btn"
          color="primary"
          rounded
          size="large"
          @click="onActionClick({ action: ZtnaAction.ADD_ZTNA_RULE })"
        >
          <v-icon icon="$add" color="white" />
          <span v-text="$t('general.add')" />
        </v-btn>
      </template>
    </v-menu>
  </div>

  <div v-if="!enabled" class="warning-block d-flex align-center my-4">
    <v-icon class="mr-3 ml-4" icon="$warning"> </v-icon>
    <span class="body2">{{ $t("network.virtualOffice.ztna.alert") }}</span>
  </div>

  <ztna-rules-table
    :pagination="pagination"
    :user-based-auth-enabled="deviceSettings.enableUserBasedAuthentication"
    :items="rules"
    :loading="loading"
    :total-items="total"
    :ztna-disabled="!enabled"
    @page-change="onPageChange"
    @action-click="onActionClick"
  />
</template>
<script lang="ts">
import { computed, defineComponent, onMounted, type PropType } from "vue";

import { useZtnaStore, type ZtnaRule } from "@/_store/network/ztna.module";
import { type DialogConfig, useDialogsStore } from "@/_store/dialogs.module";

import AddZtnaRuleModal from "@/components/modals/network/AddZtnaRuleModal.vue";
import { ModalWidth } from "@/constants/modals";
import { VirtualOfficeAction, ZtnaAction } from "@/constants/network";
import ZtnaRulesTable from "@/components/tables/ZtnaRulesTable.vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import type { VirtualOfficeSettings } from "@/_store/network/virtual-office.module";
import { useDevicesSettingsStore } from "@/_store/devices-settings.module.ts";
import VpnMobileAppModal from "@/components/modals/network/VpnMobileAppModal.vue";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  components: { ZtnaRulesTable },
  props: {
    settings: {
      type: Object as PropType<VirtualOfficeSettings>,
      required: true,
    },
  },
  emits: ["show-all-ips"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { openDialog } = useDialogsStore();
    const deviceSettingsStore = useDevicesSettingsStore();
    const ztnaStore = useZtnaStore();
    const { toggleZtnaEnabled, saveZtnaRule, getZtnaRules, updateZtnaRule, deleteZtnaRule } =
      useZtnaStore();

    const { pagination, rules, loading, enabled, total } = storeToRefs(ztnaStore);
    const { settings: deviceSettings } = storeToRefs(deviceSettingsStore);

    const onToggleZtnaEnabled = async () => {
      await toggleZtnaEnabled(!enabled.value);
      if (enabled.value) {
        await getZtnaRules();
      }
    };

    const ips = computed(() => (props.settings?.servers || []).slice(0, 5).join(", "));

    const hasManyServers = computed((): boolean => props.settings?.servers.length > 4);

    const onShowAllIpsClick = () => {
      emit("show-all-ips");
    };
    const onPageChange = async (pageObj: { page: number; itemsPerPage: number }): Promise<void> => {
      ztnaStore.$patch({
        pagination: {
          page: pageObj.page - 1,
          pageSize: pageObj.itemsPerPage,
        },
      });
      await getZtnaRules();
    };

    const onVpnMobileAppClick = () => {
      openDialog({
        ...componentDialogsConfigConstructor({
          component: VpnMobileAppModal,
          action: VirtualOfficeAction.GET_VPN_MOBILE_APP,
          width: ModalWidth.LARGE,
          callback: () => ({}),
        }),
        footer: null,
      });
    };

    const onActionClick = ({ action, item }: { action: ZtnaAction; item?: ZtnaRule }) => {
      let modalConfig;
      switch (action) {
        case ZtnaAction.ADD_ZTNA_RULE:
          modalConfig = {
            ...componentDialogsConfigConstructor({
              component: AddZtnaRuleModal,
              width: ModalWidth.LARGE,
              action: ZtnaAction.ADD_ZTNA_RULE,
              callback: saveZtnaRule,
              scrollable: true,
              customErrorHandling: true,
              showValidationAlert: true,
            }),
          };
          break;
        case ZtnaAction.DELETE_ZTNA_RULE:
          modalConfig = {
            ...confirmationDialogsConfigConstructor({
              item,
              action,
              callback: () => deleteZtnaRule(item!),
              width: ModalWidth.SMALL,
            }),
            header: {
              title: t(`modals.${action}.title`),
              titleClass: "text-red-dark",
              titleIcon: "warning",
            },
          };
          break;
        case ZtnaAction.EDIT_ZTNA_RULE:
          modalConfig = {
            ...componentDialogsConfigConstructor({
              component: AddZtnaRuleModal,
              width: ModalWidth.LARGE,
              item,
              action: ZtnaAction.EDIT_ZTNA_RULE,
              callback: updateZtnaRule,
              scrollable: true,
              customErrorHandling: true,
              showValidationAlert: true,
            }),
          };
          break;
        case ZtnaAction.TOGGLE_ZTNA:
          modalConfig = {
            ...confirmationDialogsConfigConstructor({
              callback: onToggleZtnaEnabled,
              width: ModalWidth.SMALL,
              action: enabled.value ? ZtnaAction.DISABLE_ZTNA : ZtnaAction.ENABLE_ZTNA,
              item: {},
            }),
            header: {
              title: t(
                `modals.${enabled.value ? ZtnaAction.DISABLE_ZTNA : ZtnaAction.ENABLE_ZTNA}.title`
              ),
              titleClass: "text-red-dark",
              titleIcon: "warning",
            },
          };
          break;
      }
      openDialog(modalConfig as DialogConfig);
    };

    onMounted(async () => {
      await getZtnaRules();
      await deviceSettingsStore.getDevicesSettings();
    });

    return {
      onShowAllIpsClick,
      hasManyServers,
      ips,
      enabled,
      onPageChange,
      onActionClick,
      onToggleZtnaEnabled,
      ZtnaAction,
      pagination,
      rules,
      loading,
      total,
      deviceSettings,
      onVpnMobileAppClick,
    };
  },
});
</script>
